import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getToken } from "../../../Compo/utilis/getToken";
import { BEFORE_LOGOUT_EVENT, markIdleLogout } from "../../lib/dealSession";

// The idle policy applies on EVERY route while a token is held — including the
// landing page — so a logged-in user can never sit idle anywhere past 10 min.

// Last activity is shared across tabs (and survives a closed browser / sleeping
// laptop) so: activity in any tab keeps every tab alive, and returning after
// more than 10 minutes away logs out immediately instead of restarting the clock.
const LAST_ACTIVITY_KEY = "klargo.lastActivity";
const ACTIVITY_WRITE_THROTTLE_MS = 5000;

function readSharedActivity() {
  try {
    const v = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

function writeSharedActivity(ts) {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
  } catch {
    /* storage unavailable — per-tab clock still applies */
  }
}

function clearSharedActivity() {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    /* ignore */
  }
}

// Inactivity policy (production): warn after 8 minutes without activity, then a 2-minute
// countdown, so the user is logged out at 10 minutes idle. These production values are fixed — no
// environment variable can change them in a production build. Only in development builds can
// REACT_APP_SESSION_WARN_MIN / REACT_APP_SESSION_LOGOUT_MIN (e.g. a local .env.development.local
// setting 3/4) shorten them, so the full flow can be verified in minutes.
const PRODUCTION_WARN_AFTER_MS = 8 * 60 * 1000;
const PRODUCTION_LOGOUT_AFTER_MS = PRODUCTION_WARN_AFTER_MS + 2 * 60 * 1000;
const envMinutes = (name, fallback) => {
  if (process.env.NODE_ENV === "production") return fallback;
  const raw = Number(process.env[name]);
  return Number.isFinite(raw) && raw > 0 ? raw * 60 * 1000 : fallback;
};
const WARN_AFTER_MS = envMinutes("REACT_APP_SESSION_WARN_MIN", PRODUCTION_WARN_AFTER_MS);
const LOGOUT_AFTER_MS = envMinutes("REACT_APP_SESSION_LOGOUT_MIN", PRODUCTION_LOGOUT_AFTER_MS);
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];

// Dispatched right before logout() runs so any mounted deal-flow component
// (which owns its own dealId/role/sendDealMessage — this component
// deliberately stays deal-agnostic) can tell its counterpart over SignalR and
// leave the deal group. See SellerProfileFlow.jsx / BuyerProfileFlow.jsx /
// BuyerSuccessFlow.jsx's listener for this event.
// Kept for compatibility; the event is now dispatched by useAuth().logout (see
// lib/dealSession.js) so a manual sign-out notifies the counterpart too.
export const SESSION_TIMEOUT_LOGOUT_EVENT = BEFORE_LOGOUT_EVENT;

// Reads localStorage directly (getToken() console.errors when there is none,
// which would spam the console once a second).
function hasStoredToken() {
  try {
    return Boolean(localStorage.getItem("authToken"));
  } catch {
    return false;
  }
}

// Absolute session limit = the JWT's own expiry (backend issues 8h tokens).
function getTokenExpiryMs() {
  try {
    const t = getToken();
    if (!t) return null;
    const payload = JSON.parse(
      atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload?.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export default function SessionTimeoutManager() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  // useAuth() returns a NEW logout function on every render. If doLogout
  // depended on it directly, doLogout's identity would change on every render
  // and the idle effect below (which depends on doLogout) would tear down and
  // restart — resetting lastActivity — each time the countdown state ticked,
  // so the logout threshold would never be reached. Read it through a ref so
  // doLogout (and therefore the effect) stays stable.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const { pathname } = useLocation();
  // Based on the stored token, NOT UserContext.userId: several login paths
  // (e.g. the password login box) save the token without refreshing the user
  // context, which left userId null and the idle timer silently never started.
  const [hasToken, setHasToken] = useState(hasStoredToken);
  useEffect(() => {
    const sync = () => setHasToken(hasStoredToken());
    sync();
    const id = setInterval(sync, 1000);
    window.addEventListener("storage", sync);
    return () => {
      clearInterval(id);
      window.removeEventListener("storage", sync);
    };
  }, [pathname]);
  const authed = hasToken;

  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(Math.round((LOGOUT_AFTER_MS - WARN_AFTER_MS) / 1000));

  const lastActivity = useRef(Date.now());
  const warningRef = useRef(false);
  const loggingOut = useRef(false);
  const tickRef = useRef(null);

  const doLogout = useCallback(async () => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    // Stop the polling loop immediately — logout() is async (it awaits a
    // best-effort backend call before `authed` can ever flip false), and
    // without this the still-running 1s interval kept re-entering here every
    // tick until that propagated, firing the backend Logout call repeatedly.
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    setWarning(false);
    warningRef.current = false;
    // logout() itself notifies the deal counterpart (lib/dealSession.js). Mark this as the idle
    // timeout first, so the counterpart is asked whether to wait rather than only told.
    markIdleLogout();
    try {
      await logoutRef.current();
    } finally {
      clearSharedActivity();
      loggingOut.current = false;
    }
  }, []);

  const staySignedIn = useCallback(() => {
    lastActivity.current = Date.now();
    writeSharedActivity(lastActivity.current);
    warningRef.current = false;
    setWarning(false);
  }, []);

  useEffect(() => {
    if (!authed) {
      setWarning(false);
      warningRef.current = false;
      // Logged out (any path) — the next login must start a fresh idle clock.
      clearSharedActivity();
      return;
    }

    // Resume from the shared clock (another tab, or before the browser was
    // closed); only a fresh session starts from "now".
    const stored = readSharedActivity();
    lastActivity.current = stored ?? Date.now();
    if (!stored) writeSharedActivity(lastActivity.current);
    loggingOut.current = false;
    let lastWrite = 0;

    // Reset the idle clock on activity — but not while the warning is up
    // (there the user must explicitly choose "Stay signed in").
    const onActivity = () => {
      if (warningRef.current) return;
      const now = Date.now();
      lastActivity.current = now;
      if (now - lastWrite >= ACTIVITY_WRITE_THROTTLE_MS) {
        lastWrite = now;
        writeSharedActivity(now);
      }
    };
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true })
    );

    const checkIdle = () => {
      if (loggingOut.current) return;

      // Absolute limit — the token has expired (or will within a second).
      const exp = getTokenExpiryMs();
      if (exp && Date.now() >= exp - 1000) {
        doLogout();
        return;
      }

      // Activity (or "Stay signed in") in another tab keeps this one alive too.
      const shared = readSharedActivity();
      if (shared && shared > lastActivity.current) {
        lastActivity.current = shared;
        if (warningRef.current && Date.now() - shared < WARN_AFTER_MS) {
          warningRef.current = false;
          setWarning(false);
        }
      }

      const idle = Date.now() - lastActivity.current;
      if (idle >= LOGOUT_AFTER_MS) {
        doLogout();
        return;
      }
      if (idle >= WARN_AFTER_MS) {
        if (!warningRef.current) {
          warningRef.current = true;
          setWarning(true);
        }
        setRemaining(Math.max(0, Math.ceil((LOGOUT_AFTER_MS - idle) / 1000)));
      }
    };

    tickRef.current = setInterval(checkIdle, 1000);
    // Returning after >10 min away (closed browser, sleep) logs out at once.
    checkIdle();

    // Background/hidden tabs get their setInterval throttled (or paused
    // entirely while the OS suspends the whole process) — re-check the
    // instant the tab regains focus rather than waiting up to several
    // seconds for the next throttled tick. checkIdle is wall-clock based
    // (Date.now() - lastActivity.current) so this is just promptness, not a
    // correctness fix.
    const onVisible = () => {
      if (document.visibilityState === "visible") checkIdle();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [authed, doLogout]);

  if (!authed || !warning || typeof document === "undefined") return null;

  // Portal to <body> with the top-most z-index so the countdown is visible
  // and live even while another modal (SweetAlert, BankID, …) is open; the
  // countdown also runs on its own clock rather than depending on the idle
  // poll's state updates.
  return createPortal(
    <WarningDialog
      t={t}
      deadline={lastActivity.current + LOGOUT_AFTER_MS}
      fallbackSeconds={remaining}
      onStay={staySignedIn}
      onLogout={doLogout}
    />,
    document.body
  );
}

function WarningDialog({ t, deadline, fallbackSeconds, onStay, onLogout }) {
  const calc = () => Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  const [remaining, setRemaining] = useState(Number.isFinite(deadline) ? calc() : fallbackSeconds);
  useEffect(() => {
    const id = setInterval(() => setRemaining(calc()), 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);

  const staySignedIn = onStay;
  const doLogout = onLogout;
  const mm = String(Math.floor(remaining / 60)).padStart(1, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 2147483000,
        background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <style>{".klargo-session-stay:hover{background:#1d3444 !important}"}</style>
      <div
        style={{
          background: "#fff", borderRadius: 13, maxWidth: 400, width: "100%",
          padding: "28px 24px", boxShadow: "0 12px 28px -18px rgba(16,30,40,.4)",
          border: "1px solid rgba(20,36,46,.16)",
          fontFamily: '"IBM Plex Sans", system-ui, sans-serif', textAlign: "center",
        }}
      >
        <h2 style={{ margin: "0 0 10px", fontFamily: '"Source Serif 4", Georgia, serif', fontSize: 20, fontWeight: 700, color: "#14242e" }}>
          {t("session.warningTitle")}
        </h2>
        <p style={{ margin: "0 0 20px", fontSize: 15, lineHeight: 1.6, color: "#44525c" }}>
          {t("session.warningBody", { seconds: `${mm}:${ss}` })}
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={staySignedIn}
            className="klargo-session-stay"
            style={{
              background: "#14242e", color: "#f7f9f9", border: 0, borderRadius: 9, minHeight: 44,
              padding: "12px 20px", fontSize: 15, fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
            }}
          >
            {t("session.stay")}
          </button>
          <button
            type="button"
            onClick={doLogout}
            style={{
              background: "#fff", color: "#8f2018", border: "1px solid #e2b4ad", borderRadius: 9, minHeight: 44,
              padding: "12px 20px", fontSize: 15, fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
            }}
          >
            {t("session.logout")}
          </button>
        </div>
      </div>
    </div>
  );
}
